import { PortableText } from "@portabletext/react";
import { Button, ErrorContainer, Heading, IconChevronRight, Text } from "@sentricmusic/ui";
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import type { SyncItem, SyncsAvailableResult } from "@/api/sanity/queries/getSyncs";

import { useGetMyAccount } from "@/api/queries";
import useSiteConfigAuth from "@/api/sanity/queries/getSiteConfigWithCode";
import useSyncsQuery from "@/api/sanity/queries/getSyncs";
import { hasAdminRights } from "@/auth/shared";
import LoadingSmall from "@/components/LoadingSmall";
import useAppStore from "@/store/appStateZustand";

function Syncs() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const selectedAccount = useAppStore(s => s.selectedAccount);
  const myAccount = useGetMyAccount({
    artistaccountcode: selectedAccount as string,
  });
  const adminRights = hasAdminRights(myAccount?.data);

  const { data: syncsData, isLoading, isSuccess } = useSyncsQuery();
  const { data: siteConfig } = useSiteConfigAuth();

  const allSyncsAvailable = useAppStore(s => s.allSyncsAvailable);

  function isSyncsAvailableResult(data: unknown): data is SyncsAvailableResult {
    return typeof data === "object" && data !== null && "syncs_available" in data;
  }

  const syncs: SyncItem[] = allSyncsAvailable === true
    ? (Array.isArray(syncsData) ? syncsData : [])
    : (isSyncsAvailableResult(syncsData) ? syncsData.syncs_available : []);

  const noSyncs = siteConfig?.disable_syncs;

  useEffect(() => {
    if (noSyncs) {
      void navigate({ to: "/" });
    }
  }, [noSyncs, navigate]);

  return (
    <div className="flex-1 order-last xl:w-full bg-layer-raised min-h-50 lg:min-h-screen-minus-header">
      <Heading as="h3" className="mb-8">{t("other:browse-syncs-header", "Browse Syncs")}</Heading>
      <div className="w-full flex flex-col gap-6">
        {isLoading
          ? <LoadingSmall />
          : isSuccess && syncs.length > 0
            ? (
                syncs.map((sync: SyncItem) => (
                  <div key={sync._id} className="flex flex-row overflow-hidden bg-white rounded-md rounded-base shadow-xs">
                    <div className="flex flex-col gap-4 flex-1 p-4">
                      <div>
                        <Text className="mb-2 text-lg font-semibold">{sync.name}</Text>
                        <div className="html-content">
                          <PortableText value={sync.description} />
                        </div>
                      </div>
                      <div className="mt-4">
                        <Button
                          variant="primary"
                          label={t("other:apply", "Apply")}
                          icon={<IconChevronRight size="md" className="stroke-ink-primary" />}
                          onClick={
                            adminRights
                              ? () => { void navigate({ to: `/syncs/apply/${sync._id}` }); }
                              : undefined
                          }
                          disabled={!adminRights}
                        />
                      </div>
                    </div>
                    <img
                      className="hidden object-cover xl:block xl:w-64 xl:shrink-0 self-stretch"
                      src={sync.imageUrl}
                      alt={sync.altText}
                    />
                  </div>
                ))
              )
            : <ErrorContainer message={t("other:no-syncs", "No Syncs Available")} />}
      </div>
    </div>
  );
}
export default Syncs;
